import React from 'react';
import axios from 'axios';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Provider as BusProvider } from 'react-bus';
import _get from 'lodash/get';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import RedBox from 'redbox-react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import ReactDOM from 'react-dom/client';

import createStore from './store/createStore';
import './styles/layout.scss';
import { buildMuiTheme } from 'src/muiTheme';
import { SplitSdk, SplitFactory } from '@splitsoftware/splitio-react';
import queryClient from './queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { StacklineThemeProvider } from '@stackline/ui';
import RouterPropsProvider from './providers/RouterPropsProvider';
import { LicenseInfo } from '@mui/x-license-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import ExportContextProvider from 'src/providers/ExportContextProvider';
import SnackbarProvider from './providers/SnackbarProvider';
import PopupProvider from 'src/providers/PopupProvider';
import UserProfileImageProvider from './providers/UserProfileImageProvider';
import NotificationProvider from 'src/providers/NotificationProvider';
import { BulkAdjustmentProvider } from './components/BeaconV2Components/ExperimentalLayout/Forecasts/BulkAdjustments/BulkAdjustmentProvider';
import { PollingContextProvider } from 'src/providers/PollingContextProvider';
import AppModals from 'src/components/AddModals';
import UserTrackingInitializer from './components/UserTrackingInitializer';
import { useKonamiRaveMode } from './hooks/useKonamiCode';
import { isV2Enabled } from 'src/utils/authVersion';
import { useLocation } from 'src/utils/Hooks';
import { setCognitoIdToken } from 'src/services/advisorContext/cognitoTokenStore';
import { isSsoHost } from 'src/utils/ssoReferrer';

// V1 sign-in handoff from the SSO host (signin.stackline.com / enterprise-sso.stackline.com): SignInV2Container
// appends ?cognito_id_token=… on the cross-origin redirect because neither
// the Amplify session nor the sl-cognito-id-token sessionStorage entry
// survives the origin change. Persist the token here (atlas/beacon origin)
// so getAuthHeader() in src/services/advisorContext can attach
// `Authorization: Bearer <idToken>` to advisor requests, then strip the
// param from the URL so it doesn't sit in history.
//
// Must run synchronously before any advisor call fires; placing it at the
// top of main.tsx (pre-render) is sufficient.
(() => {
  try {
    const url = new URL(window.location.href);
    const cognitoIdToken = url.searchParams.get('cognito_id_token');
    if (!cognitoIdToken) return;
    setCognitoIdToken(cognitoIdToken);
    url.searchParams.delete('cognito_id_token');
    window.history.replaceState({}, '', url.toString());
  } catch {
    // ignore — bad URL or storage error is non-fatal for app boot
  }
})();

const AppV1 = React.lazy(() => import('./components/App/App').then(m => ({ default: m.default as any })));
const AppV2 = React.lazy(() => import('./components/App/App_v2').then(m => ({ default: m.default as any })));
const AuthShell = React.lazy(() => import('./components/AuthShell/AuthShell'));

// Auth routes are shell-agnostic: AuthShell mounts directly without making
// any V1/V2 decision. The decision is deferred until after the form submits
// (the API has now issued v1 or v2 cookies + the sl-auth-version marker), so
// the next mount of AppSwitch picks the right heavy shell on its first read.
const AUTH_ROUTES = new Set([
  '/sign_in',
  '/sign_up_invite',
  '/password_reset',
  '/sign_out',
  '/auth/callback',
  '/switch-division',
  '/no-access'
]);

/**
 * `/` is an auth route ONLY on the SSO host (signin.stackline.com or the
 * legacy enterprise-sso alias) — there it's the canonical sign-in URL.
 * On any other host `/` is the regular app home.
 */
function isAuthPath(pathname: string, host: string): boolean {
  if (AUTH_ROUTES.has(pathname)) return true;
  return pathname === '/' && isSsoHost(host);
}

// Component that activates Konami code "rave mode" easter egg
function KonamiCodeHandler() {
  useKonamiRaveMode();
  return null;
}

function AppSwitch() {
  // Auth paths short-circuit the V1/V2 decision and mount AuthShell directly.
  // Reactive (useLocation) so history.push('/sign_out') from a nav menu
  // actually unmounts the heavy shell and mounts AuthShell — without this,
  // the in-app /sign_out route was being handled by AppV2's internal Switch
  // and resolving to V1 SignOut, leaving sl-auth-token uncleared. Tearing
  // down the heavy shell on /sign_out is exactly what we want; the SignOut
  // component immediately redirects via window.location.href anyway.
  const location = useLocation();
  const isAuthRoute = isAuthPath(location.pathname, window.location.host);

  // Lock the V1/V2 shell decision at mount. Re-evaluating isV2Enabled() on
  // every render risks AppV2 → AppV1 swaps mid-session, which re-runs both
  // shells' constructors. That double-fires non-idempotent side effects
  // (numeral formatters, mixpanel/sentry/logrocket gating, axios config,
  // Amplify hub listeners) and crashes the page. The marker cookie is stable
  // for the lifetime of a session, so this useState lock is belt-and-suspenders.
  const [useV2Flow] = React.useState(() => {
    if (isAuthPath(window.location.pathname, window.location.host)) return false; // unused for auth routes
    const onSsoHost = isSsoHost(window.location.host);
    return onSsoHost || isV2Enabled();
  });

  if (isAuthRoute) {
    return (
      <React.Suspense fallback={null}>
        <AuthShell />
      </React.Suspense>
    );
  }

  const AppComponent = useV2Flow ? AppV2 : AppV1;

  const inner = (
    <React.Suspense fallback={null}>
      <>
        {(() => {
          const { ContentGenerationsService } = require('src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useAllGenerationsService');
          return <ContentGenerationsService />;
        })()}
        {(() => {
          const { SegmentProcessingPoller } = require('src/components/AtlasV2Components/Hooks/useFetchProcessingSegments');
          return <SegmentProcessingPoller />;
        })()}
        <KonamiCodeHandler />
        <AppModals />
        <AppComponent />
      </>
    </React.Suspense>
  );

  return inner;
}

// ========================================================
// A/B Test Provider Instantiation (Split.io)
// ========================================================
export const factory = SplitSdk({
  core: {
    authorizationKey: 'fm4ur66cflc3lt0jjrh6j3olsu0iebp4ji1j',
    key: 'key'
  },
  startup: {
    requestTimeoutBeforeReady: 5,
    readyTimeout: 5
  },
  debug: __DEV__ ? 'WARN' : 'ERROR'
});

// ========================================================
// Store Instantiation
// ========================================================
export const store = createStore();

store.subscribe(() => {
  store.getState();
});

// AuthShell mounts before AppV1/AppV2 (which call configureAxios), so
// without setting these defaults early any pre-login axios call (e.g.
// SignUp's GetUserByInviteCode / UserSignupByInvite) goes to the current
// origin instead of the API host. Cookies returned end up scoped to the
// app subdomain and are invisible to the post-reload fetchConfig that hits
// the API host directly. AppV1/AppV2 still call full configureAxios in
// their constructors (idempotent overwrite + interceptors).
axios.defaults.baseURL = `https://${store.getState().app.targetUrl}`;
axios.defaults.withCredentials = true;

// ========================================================
// MUI X License
// ========================================================
LicenseInfo.setLicenseKey(
  'cf5cbed7d3487f7270e48f115ee63a45T1JERVI6Mzk5MjEsRVhQSVJZPTE2NzkxNzQyMzcwMDAsS0VZVkVSU0lPTj0x'
);

// ========================================================
// Render Setup
// ========================================================
const MOUNT_NODE = document.getElementById('root');

// Create root instance
const root = ReactDOM.createRoot(MOUNT_NODE);

let render = () => {
  root.render(
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          <BusProvider>
            <SplitFactory factory={factory}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={buildMuiTheme()}>
                  <StacklineThemeProvider>
                    <BrowserRouter basename="/">
                      <NotificationProvider>
                        <PopupProvider>
                          <SnackbarProvider>
                            <RouterPropsProvider>
                              <ExportContextProvider>
                                <UserProfileImageProvider>
                                  <PollingContextProvider>
                                    <BulkAdjustmentProvider>
                                      <AppSwitch />
                                    </BulkAdjustmentProvider>
                                  </PollingContextProvider>
                                </UserProfileImageProvider>
                              </ExportContextProvider>
                            </RouterPropsProvider>
                          </SnackbarProvider>
                        </PopupProvider>
                      </NotificationProvider>
                    </BrowserRouter>
                  </StacklineThemeProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </SplitFactory>
          </BusProvider>
        </QueryClientProvider>
      </Provider>
    </LocalizationProvider>
  );
};

// This code is excluded from production bundle
if (__DEV__) {
  if (module.hot) {
    // Development render functions
    const renderApp = render;
    const renderError = (error) => {
      root.render(<RedBox error={error} />);
    };

    // Wrap render in try/catch
    render = () => {
      try {
        renderApp();
      } catch (error) {
        renderError(error);
      }
    };

    // Update the hot module replacement setup
    module.hot.accept([], () => {
      // Re-render using the root instance
      render();
    });
  }
}

if (store.getState().app.stage === 'dev') {
  // Enable these on dev and localhost
  // Make the Redux store available as a global variable if in debug mode
  (window as any).store = store;
  (window as any).getState = () => store.getState();
  (window as any).ess = () => store.getState().entitySearchService;
  (window as any).getMainEntity = () => _get(store.getState(), ['entityService', 'mainEntity']);
  (window as any).queryClient = queryClient;
  (window as any).DEBUG_MODE = false;
  (window as any).enableLogging = () => {
    (window as any).DEBUG_MODE = true;
    // Update Logger's debug mode
    import('./services/userTracking/utils/Logger').then(({ Logger }) => {
      Logger.getInstance().setDebugMode(true);
    });
  };
  (window as any).disableLogging = () => {
    (window as any).DEBUG_MODE = false;
    // Update Logger's debug mode
    import('./services/userTracking/utils/Logger').then(({ Logger }) => {
      Logger.getInstance().setDebugMode(false);
    });
  };
}

// ========================================================
// Go!
// ========================================================
if (process.env.NODE_ENV !== 'test') {
  render();
}
